import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { OptimizerDescriptionRendererComponent } from "./optimizer-description.renderer.component";
import { IDateRange, ITableTdDirective, IToggleComponent, ITooltipDirective, ToasterService } from "@front/m19-ui";
import { StrategyLinkComponent } from "@m19-board/shared/strategy-link/strategy-link.component";
import { StrategyEx } from "@front/m19-models";
import { AlgoMode, CampaignType } from "@front/m19-api-client";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountSelectionService,
  BaseStrategyService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
} from "@front/m19-services";
import { TranslocoService } from "@jsverse/transloco";
import { PercentPipe } from "@angular/common";
import { CampaignTypeBadgeComponent } from "@m19-board/shared/campaign-type-badge/campaign-type-badge.component";

@Component({
  selector: "[app-strategy-boost-list-row]",
  imports: [
    OptimizerDescriptionRendererComponent,
    IToggleComponent,
    StrategyLinkComponent,
    PercentPipe,
    ITableTdDirective,
    ITooltipDirective,
    CampaignTypeBadgeComponent,
  ],
  templateUrl: "./promo-days-boost-list-row.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoDaysBoostListRowComponent {
  readonly accountSelectionService = inject(AccountSelectionService);
  readonly translocoService = inject(TranslocoService);
  readonly toasterService = inject(ToasterService);
  readonly spStrategyService = inject(SpStrategyService);
  readonly sbStrategyService = inject(SbStrategyService);
  readonly sdStrategyService = inject(SdStrategyService);

  strategy = input.required<StrategyEx>();
  dateRange = input<IDateRange | null>(null);

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  readonly isPromoDaysBoostEnabled = computed(() => this.strategy().primeDayBoost !== undefined);

  readonly isPromoDaysEligible = computed(() => {
    return !(
      this.strategy().algoMode !== AlgoMode.ACOS_TARGET ||
      this.isReadOnly() ||
      (this.strategy()!.dailyBudget && this.strategy()!.dailyBudget! > 0)
    );
  });

  readonly ineligibleReason = computed(() => {
    const strategy = this.strategy();
    if (strategy.algoMode === AlgoMode.PRODUCT_LAUNCH) {
      return this.translocoService.translate("strategy-boost-table.daily_target");
    } else if (strategy.algoMode === AlgoMode.MONTHLY_BUDGET_TARGET) {
      return this.translocoService.translate("strategy-boost-table.monthly_budget");
    } else if (strategy.dailyBudget && strategy.dailyBudget > 0) {
      return this.translocoService.translate("strategy-boost-table.avg_daily_spend");
    }
    return undefined;
  });

  promoDaysCallLoading = signal<boolean>(false);

  toggleBoost() {
    const strategy = this.strategy();
    const primeDayBoost = strategy.primeDayBoost == undefined ? 25 : undefined;
    this.promoDaysCallLoading.set(true);

    // TODO(joseph): Refactor to avoid switch case
    let strategyService: BaseStrategyService<CampaignType>;
    switch (strategy.campaignType) {
      case CampaignType.SP:
        strategyService = this.spStrategyService;
        break;
      case CampaignType.SB:
        strategyService = this.sbStrategyService;
        break;
      case CampaignType.SD:
        strategyService = this.sdStrategyService;
        break;
      default:
        throw new Error(`Unsupported campaign type: ${strategy.campaignType}`);
    }

    strategyService
      .updateStrategyPrimeDayBoost(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.accountMarketplace().resourceOrganizationId!,
        strategy.strategyId,
        primeDayBoost,
      )
      .subscribe({
        next: () => {
          this.promoDaysCallLoading.set(false);
          const message = primeDayBoost
            ? this.translocoService.translate("strategy-boost.promo_days_boost_activated_for_strategy", {
                0: strategy.name,
              })
            : this.translocoService.translate("strategy-boost.promo_days_boost_deactivated_for_strategy", {
                0: strategy.name,
              });
          this.toasterService.success(message);
        },
        error: (error) => {
          this.promoDaysCallLoading.set(false);
          this.toasterService.error(error.message);
        },
      });
  }
}
